import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  Utensils, 
  FileText, 
  Calculator, 
  ArrowRight, 
  CheckCircle2,
  Shield,
  Users,
  Sparkles,
  Compass,
  Trophy,
  Lock,
  Star,
  ExternalLink,
  Menu,
  X
} from 'lucide-react';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  type CarouselApi,
} from '../components/ui/carousel';

interface Tool {
  name: string;
  description: string;
  longDescription: string;
  icon: React.ReactNode;
  url: string;
  status: 'live' | 'coming-soon';
  features: string[];
  color: string;
}

const tools: Tool[] = [
  {
    name: 'Dining',
    description: 'Complete event and dining management for your unit',
    longDescription: 'Streamline your festive board arrangements with our comprehensive dining management system. From event creation to payment processing, seating charts to kitchen reports, we have everything covered for elegant event orchestration.',
    icon: <Utensils className="w-12 h-12" />,
    url: 'https://dining.masonic-tools.co.uk',
    status: 'live',
    features: [
      'Event planning & management',
      'Venue management system',
      'RSVP tracking with dietary requirements',
      'Event invitation system',
      'Waitlist management',
      'Seating arrangements & table planner',
      'Professional place card generation',
      'Kitchen reports & catering details',
      'Event announcements & communications',
      'Stripe payment integration',
      'QR code check-in system',
      'Analytics & reporting dashboards',
      'Multiple unit membership support',
      'Personal dashboard for all events',
    ],
    color: 'bg-blue-900',
  },
  {
    name: 'Secretary',
    description: 'Streamline your unit administrative tasks',
    longDescription: 'Manage all your unit administration efficiently. Keep member records, meeting minutes, and communications organized in one secure platform.',
    icon: <FileText className="w-12 h-12" />,
    url: 'https://secretary.masonic-tools.co.uk',
    status: 'coming-soon',
    features: [
      'Member records management',
      'Meeting minutes',
      'Document management',
      'Unit communications',
      'Attendance tracking',
      'Automated notifications',
    ],
    color: 'bg-blue-800',
  },
  {
    name: 'Treasurer',
    description: 'Professional financial management for your unit',
    longDescription: 'Take control of your unit finances with powerful tools for budgeting, reporting, and subscription management. Built for transparency and accountability.',
    icon: <Calculator className="w-12 h-12" />,
    url: 'https://treasurer.masonic-tools.co.uk',
    status: 'coming-soon',
    features: [
      'Financial tracking',
      'Budget management',
      'Subscription management',
      'Payment processing',
      'Financial reporting',
      'Audit trails',
    ],
    color: 'bg-blue-900',
  },
];

const HomePage = () => {
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    if (!api) {
      return;
    }

    setCurrent(api.selectedScrollSnap());

    api.on('select', () => {
      setCurrent(api.selectedScrollSnap());
    });
  }, [api]);

  // Auto-advance carousel
  useEffect(() => {
    if (!api) {
      return;
    }

    const interval = setInterval(() => {
      api.scrollNext();
    }, 5000);

    return () => clearInterval(interval);
  }, [api]);

  return (
    <div className="min-h-screen bg-white">
      {/* Decorative top border */}
      <div className="h-1 bg-gradient-to-r from-yellow-400 via-yellow-500 to-yellow-400" />
      
      {/* Header */}
      <header className="bg-white border-b border-gray-100 sticky top-0 z-50 shadow-sm backdrop-blur-sm bg-white/95">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 sm:py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2 sm:space-x-4">
              <div className="relative">
                <div className="absolute inset-0 bg-yellow-400 rounded-xl blur-md opacity-30" />
                <div className="relative w-10 h-10 sm:w-14 sm:h-14 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-xl flex items-center justify-center shadow-lg">
                  <Compass className="w-5 h-5 sm:w-8 sm:h-8 text-white" />
                </div>
              </div>
              <div>
                <h1 className="text-xl sm:text-2xl md:text-3xl font-display font-bold text-blue-900 tracking-tight">
                  Masonic Tools
                </h1>
                <p className="text-xs sm:text-sm text-gray-500 font-serif italic">Professional Unit Management</p>
              </div>
            </div>
            <nav className="hidden md:flex space-x-8">
              <a href="#tools" className="text-gray-600 hover:text-yellow-600 transition-colors font-medium tracking-wide">Tools</a>
              <a href="#features" className="text-gray-600 hover:text-yellow-600 transition-colors font-medium tracking-wide">Features</a>
              <a href="#mission" className="text-gray-600 hover:text-yellow-600 transition-colors font-medium tracking-wide">Mission</a>
            </nav>
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 text-gray-600 hover:text-yellow-600 transition-colors"
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <nav className="md:hidden mt-4 pb-4 border-t border-gray-100">
              <div className="flex flex-col space-y-3 pt-4">
                <a 
                  href="#tools" 
                  onClick={() => setMobileMenuOpen(false)}
                  className="text-gray-600 hover:text-yellow-600 transition-colors font-medium tracking-wide py-2"
                >
                  Tools
                </a>
                <a 
                  href="#features" 
                  onClick={() => setMobileMenuOpen(false)}
                  className="text-gray-600 hover:text-yellow-600 transition-colors font-medium tracking-wide py-2"
                >
                  Features
                </a>
                <a 
                  href="#mission" 
                  onClick={() => setMobileMenuOpen(false)}
                  className="text-gray-600 hover:text-yellow-600 transition-colors font-medium tracking-wide py-2"
                >
                  Mission
                </a>
              </div>
            </nav>
          )}
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-b from-gray-50 to-white px-4 sm:px-6 lg:px-8 py-12 sm:py-16 md:py-20 lg:py-28 overflow-hidden">
        {/* Decorative elements */}
        <div className="absolute top-20 left-10 opacity-5 hidden sm:block">
          <Star className="w-24 h-24 text-yellow-500" />
        </div>
        <div className="absolute bottom-20 right-10 opacity-5 hidden sm:block">
          <Star className="w-32 h-32 text-yellow-500" />
        </div>
        
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-8 sm:mb-12 md:mb-16"
          >
            <div className="mb-4 sm:mb-6">
              <span className="inline-block px-3 py-1.5 sm:px-4 sm:py-2 bg-yellow-50 text-yellow-700 rounded-full text-xs sm:text-sm font-semibold tracking-wide border border-yellow-200">
                PROFESSIONAL SUITE OF APPLICATIONS
              </span>
            </div>
            <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl 2xl:text-8xl font-display font-bold mb-4 sm:mb-6 md:mb-8 text-blue-900 leading-tight tracking-tight px-2">
              Professional Tools for
              <br />
              <span className="relative inline-block">
                <span className="text-yellow-600">Masonic Excellence</span>
                <div className="absolute -bottom-1 sm:-bottom-2 left-0 right-0 h-0.5 sm:h-1 bg-yellow-400/30" />
              </span>
            </h2>
            <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-gray-600 mb-4 max-w-3xl mx-auto font-serif leading-relaxed px-2">
              A comprehensive suite of applications designed to elevate your unit operations
            </p>
            <div className="flex items-center justify-center gap-2 text-gray-400">
              <div className="w-8 sm:w-12 h-px bg-gray-300" />
              <Compass className="w-3 h-3 sm:w-4 sm:h-4" />
              <div className="w-8 sm:w-12 h-px bg-gray-300" />
            </div>
          </motion.div>

          {/* Carousel */}
          <div id="tools" className="max-w-6xl mx-auto px-2 sm:px-4 md:px-8 lg:px-12">
            <Carousel
              setApi={setApi}
              opts={{
                align: 'start',
                loop: true,
              }}
              className="w-full"
            >
              <CarouselContent className="-ml-2 md:-ml-4">
                {tools.map((tool, index) => (
                  <CarouselItem key={tool.name} className="pl-2 md:pl-4">
                    <motion.div
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.5 }}
                      className="p-1 sm:p-2"
                    >
                      <div className="relative overflow-hidden rounded-xl sm:rounded-2xl bg-white border border-gray-200 shadow-xl sm:shadow-2xl hover:shadow-2xl sm:hover:shadow-3xl transition-shadow duration-300">
                        {/* Decorative corner accents */}
                        <div className="absolute top-0 left-0 w-12 h-12 sm:w-20 sm:h-20 border-t-2 border-l-2 border-yellow-400/40" />
                        <div className="absolute bottom-0 right-0 w-12 h-12 sm:w-20 sm:h-20 border-b-2 border-r-2 border-yellow-400/40" />
                        
                        <div className="p-4 sm:p-6 md:p-10 lg:p-14">
                          <div className="flex flex-col md:flex-row items-center gap-4 sm:gap-6 md:gap-10">
                            {/* Icon */}
                            <div className="relative flex-shrink-0">
                              <div className="absolute inset-0 bg-blue-900 rounded-xl sm:rounded-2xl blur-xl opacity-20" />
                              <div className={`relative w-24 h-24 sm:w-28 sm:h-28 md:w-36 md:h-36 rounded-xl sm:rounded-2xl ${tool.color} p-6 sm:p-8 md:p-10 shadow-xl sm:shadow-2xl flex items-center justify-center transform hover:scale-105 transition-transform duration-300`}>
                                <div className="text-white scale-75 sm:scale-100">
                                  {tool.icon}
                                </div>
                              </div>
                            </div>

                            {/* Content */}
                            <div className="flex-1 text-center md:text-left w-full">
                              <div className="flex flex-col sm:flex-row items-center justify-center md:justify-start gap-2 sm:gap-3 mb-3 sm:mb-4">
                                <h3 className="text-3xl sm:text-4xl md:text-5xl font-display font-bold text-blue-900">{tool.name}</h3>
                                <div className="flex items-center gap-2">
                                  {tool.status === 'live' ? (
                                    <>
                                      <span className="px-3 py-1 sm:px-4 sm:py-1.5 bg-green-50 text-green-700 text-xs font-bold rounded-full border border-green-200 tracking-wider whitespace-nowrap">
                                        LIVE
                                      </span>
                                      <a
                                        href={tool.url}
                                        className="p-2 sm:p-2.5 bg-blue-900 hover:bg-blue-800 text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-110"
                                        title={`Visit ${tool.name}`}
                                      >
                                        <ExternalLink className="w-4 h-4 sm:w-5 sm:h-5" />
                                      </a>
                                    </>
                                  ) : (
                                    <span className="px-3 py-1 sm:px-4 sm:py-1.5 bg-yellow-50 text-yellow-700 text-xs font-bold rounded-full border border-yellow-200 tracking-wider whitespace-nowrap">
                                      COMING SOON
                                    </span>
                                  )}
                                </div>
                              </div>
                              <p className="text-lg sm:text-xl md:text-2xl text-gray-700 mb-2 sm:mb-3 md:mb-4 font-serif font-medium">{tool.description}</p>
                              <p className="text-sm sm:text-base md:text-lg text-gray-600 mb-4 sm:mb-6 md:mb-8 leading-relaxed">{tool.longDescription}</p>
                              
                              {tool.status === 'live' ? (
                                <a
                                  href={tool.url}
                                  className={`inline-flex items-center justify-center w-full sm:w-auto px-6 sm:px-8 md:px-10 py-3 sm:py-3.5 md:py-4 ${tool.color} text-white text-sm sm:text-base font-semibold rounded-lg sm:rounded-xl hover:shadow-xl transition-all duration-300 transform hover:-translate-y-0.5`}
                                >
                                  Launch {tool.name}
                                  <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 ml-2" />
                                </a>
                              ) : (
                                <button
                                  disabled
                                  className="inline-flex items-center justify-center w-full sm:w-auto px-6 sm:px-8 md:px-10 py-3 sm:py-3.5 md:py-4 bg-gray-100 text-gray-400 text-sm sm:text-base font-semibold rounded-lg sm:rounded-xl cursor-not-allowed"
                                >
                                  Coming Soon
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  </CarouselItem>
                ))}
              </CarouselContent>
              <CarouselPrevious className="hidden sm:flex -left-4 md:-left-12" />
              <CarouselNext className="hidden sm:flex -right-4 md:-right-12" />
            </Carousel>

            {/* Carousel indicators */}
            <div className="flex justify-center gap-2 sm:gap-3 mt-4 sm:mt-6 md:mt-8">
              {tools.map((_, index) => (
                <button
                  key={index}
                  onClick={() => api?.scrollTo(index)}
                  className={`h-2 rounded-full transition-all duration-300 touch-manipulation ${
                    current === index ? 'bg-yellow-600 w-8 sm:w-12' : 'bg-gray-300 w-2 hover:bg-gray-400'
                  }`}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Divider */}
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex items-center justify-center py-8">
          <div className="h-px bg-gradient-to-r from-transparent via-gray-300 to-transparent w-full max-w-md" />
        </div>
      </div>

      {/* Detailed Features Section */}
      <section id="features" className="relative bg-white px-4 sm:px-6 lg:px-8 py-12 sm:py-16 md:py-20 lg:py-28">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-8 sm:mb-12 md:mb-16 lg:mb-20"
          >
            <h3 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-display font-bold mb-3 sm:mb-4 md:mb-6 text-blue-900 tracking-tight px-2">
              Comprehensive Features
            </h3>
            <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-2xl mx-auto font-serif px-2">
              Each tool is crafted with precision to serve the unique needs of your unit
            </p>
            <div className="flex items-center justify-center gap-2 mt-4 sm:mt-6 text-gray-400">
              <div className="w-8 sm:w-12 h-px bg-gray-300" />
              <Compass className="w-3 h-3 sm:w-4 sm:h-4" />
              <div className="w-8 sm:w-12 h-px bg-gray-300" />
            </div>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 md:gap-10">
            {tools.map((tool, index) => (
              <motion.div
                key={tool.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="group bg-white border-2 border-gray-100 rounded-xl sm:rounded-2xl p-6 sm:p-8 md:p-10 hover:border-yellow-300 hover:shadow-xl sm:hover:shadow-2xl transition-all duration-300"
              >
                <div className="flex items-center justify-between mb-4 sm:mb-6 md:mb-8">
                  <div className="relative">
                    <div className="absolute inset-0 bg-blue-900 rounded-xl blur-lg opacity-0 group-hover:opacity-20 transition-opacity duration-300" />
                    <div className={`relative w-16 h-16 sm:w-20 sm:h-20 rounded-xl ${tool.color} p-4 sm:p-5 shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                      <div className="text-white scale-75 sm:scale-100">
                        {tool.icon}
                      </div>
                    </div>
                  </div>
                  {tool.status === 'live' && (
                    <a
                      href={tool.url}
                      className="p-2.5 sm:p-3 bg-blue-900 hover:bg-blue-800 text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-110"
                      title={`Visit ${tool.name}`}
                    >
                      <ExternalLink className="w-4 h-4 sm:w-5 sm:h-5" />
                    </a>
                  )}
                </div>
                <h4 className="text-2xl sm:text-2xl md:text-3xl font-display font-bold text-blue-900 mb-3 sm:mb-4">{tool.name}</h4>
                <p className="text-gray-600 mb-4 sm:mb-6 md:mb-8 font-serif text-base sm:text-lg leading-relaxed">{tool.description}</p>
                <ul className="space-y-2 sm:space-y-3 md:space-y-4">
                  {tool.features.map((feature) => (
                    <li key={feature} className="flex items-start">
                      <CheckCircle2 className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-600 mr-2 sm:mr-3 flex-shrink-0 mt-0.5 sm:mt-1" />
                      <span className="text-sm sm:text-base text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission Statement Section */}
      <section id="mission" className="relative bg-gradient-to-br from-blue-900 via-blue-800 to-blue-900 px-4 sm:px-6 lg:px-8 py-12 sm:py-16 md:py-20 lg:py-28 overflow-hidden">
        {/* Decorative elements */}
        <div className="absolute top-0 right-0 w-64 h-64 sm:w-96 sm:h-96 bg-yellow-400 rounded-full blur-3xl opacity-5" />
        <div className="absolute bottom-0 left-0 w-64 h-64 sm:w-96 sm:h-96 bg-yellow-400 rounded-full blur-3xl opacity-5" />
        
        <div className="max-w-7xl mx-auto relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <div className="relative inline-block mb-4 sm:mb-6 md:mb-8">
              <div className="absolute inset-0 bg-yellow-400 rounded-full blur-2xl opacity-30" />
              <div className="relative p-4 sm:p-5 md:p-6 bg-yellow-500 rounded-full shadow-xl sm:shadow-2xl">
                <Compass className="w-10 h-10 sm:w-12 sm:h-12 md:w-16 md:h-16 text-white" />
              </div>
            </div>
            <h3 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-display font-bold mb-4 sm:mb-6 md:mb-8 text-white tracking-tight px-2">
              Our Mission
            </h3>
            <p className="text-lg sm:text-xl md:text-2xl lg:text-3xl text-blue-100 mb-6 sm:mb-8 md:mb-10 max-w-4xl mx-auto leading-relaxed font-serif px-2">
              To provide our members with the finest digital tools, crafted with precision and care, 
              enabling units to focus on what truly matters: fellowship, charity, and the pursuit of light.
            </p>
            <div className="flex items-center justify-center gap-2 sm:gap-3 mb-6 sm:mb-8 md:mb-12 text-blue-300">
              <div className="w-8 sm:w-12 md:w-16 h-px bg-blue-300/30" />
              <Star className="w-4 h-4 sm:w-5 sm:h-5" />
              <div className="w-8 sm:w-12 md:w-16 h-px bg-blue-300/30" />
            </div>
            <p className="text-base sm:text-lg md:text-xl text-blue-200 max-w-3xl mx-auto leading-relaxed mb-8 sm:mb-12 md:mb-16 px-2">
              We believe that modern technology should serve timeless traditions. Our suite of tools 
              is designed to reduce administrative burden, enhance communication, and strengthen the 
              bonds of brotherhood. By embracing innovation while honoring our heritage, we empower 
              units to operate with excellence and devote more time to the noble work of Freemasonry.
            </p>

            {/* Mission Pillars */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 mt-8 sm:mt-12 md:mt-16">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl sm:rounded-2xl p-6 sm:p-8 border border-white/20 hover:bg-white/15 transition-all duration-300">
                <div className="relative inline-block mb-4 sm:mb-6">
                  <div className="absolute inset-0 bg-yellow-400 rounded-full blur-lg opacity-30" />
                  <Trophy className="relative w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 text-yellow-400" />
                </div>
                <h4 className="text-xl sm:text-2xl font-display font-semibold text-white mb-2 sm:mb-3">Excellence</h4>
                <p className="text-sm sm:text-base text-blue-100 leading-relaxed">
                  Delivering the highest quality tools with attention to every detail
                </p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl sm:rounded-2xl p-6 sm:p-8 border border-white/20 hover:bg-white/15 transition-all duration-300">
                <div className="relative inline-block mb-4 sm:mb-6">
                  <div className="absolute inset-0 bg-yellow-400 rounded-full blur-lg opacity-30" />
                  <Lock className="relative w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 text-yellow-400" />
                </div>
                <h4 className="text-xl sm:text-2xl font-display font-semibold text-white mb-2 sm:mb-3">Security</h4>
                <p className="text-sm sm:text-base text-blue-100 leading-relaxed">
                  Protecting your unit data with enterprise-grade security measures
                </p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl sm:rounded-2xl p-6 sm:p-8 border border-white/20 hover:bg-white/15 transition-all duration-300">
                <div className="relative inline-block mb-4 sm:mb-6">
                  <div className="absolute inset-0 bg-yellow-400 rounded-full blur-lg opacity-30" />
                  <Users className="relative w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 text-yellow-400" />
                </div>
                <h4 className="text-xl sm:text-2xl font-display font-semibold text-white mb-2 sm:mb-3">Community</h4>
                <p className="text-sm sm:text-base text-blue-100 leading-relaxed">
                  Built by brethren, for brethren, fostering connection and collaboration
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="relative bg-gradient-to-b from-white to-gray-50 px-4 sm:px-6 lg:px-8 py-12 sm:py-16 md:py-20 lg:py-28">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-8 sm:mb-12 md:mb-16 lg:mb-20"
          >
            <h3 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-display font-bold mb-3 sm:mb-4 md:mb-6 text-blue-900 tracking-tight px-2">
              Why Choose Masonic Tools?
            </h3>
            <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-2xl mx-auto font-serif px-2">
              Built specifically for Masonic Units with modern technology and best practices
            </p>
            <div className="flex items-center justify-center gap-2 mt-4 sm:mt-6 text-gray-400">
              <div className="w-8 sm:w-12 h-px bg-gray-300" />
              <Compass className="w-3 h-3 sm:w-4 sm:h-4" />
              <div className="w-8 sm:w-12 h-px bg-gray-300" />
            </div>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 md:gap-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="group bg-white border-2 border-gray-100 rounded-xl sm:rounded-2xl p-6 sm:p-8 md:p-10 text-center hover:border-yellow-300 hover:shadow-xl sm:hover:shadow-2xl transition-all duration-300"
            >
              <div className="relative inline-block mb-4 sm:mb-6 md:mb-8">
                <div className="absolute inset-0 bg-blue-900 rounded-xl blur-lg opacity-0 group-hover:opacity-20 transition-opacity duration-300" />
                <div className="relative w-16 h-16 sm:w-20 sm:h-20 bg-blue-900 rounded-xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
                  <Shield className="w-8 h-8 sm:w-10 sm:h-10 text-white" />
                </div>
              </div>
              <h4 className="text-2xl sm:text-2xl md:text-3xl font-display font-semibold text-blue-900 mb-3 sm:mb-4">Secure & Private</h4>
              <p className="text-sm sm:text-base md:text-lg text-gray-600 leading-relaxed">
                Enterprise-grade security with encrypted data, role-based access control, 
                and compliance with data protection regulations.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              viewport={{ once: true }}
              className="group bg-white border-2 border-gray-100 rounded-xl sm:rounded-2xl p-6 sm:p-8 md:p-10 text-center hover:border-yellow-300 hover:shadow-xl sm:hover:shadow-2xl transition-all duration-300"
            >
              <div className="relative inline-block mb-4 sm:mb-6 md:mb-8">
                <div className="absolute inset-0 bg-yellow-600 rounded-xl blur-lg opacity-0 group-hover:opacity-20 transition-opacity duration-300" />
                <div className="relative w-16 h-16 sm:w-20 sm:h-20 bg-yellow-600 rounded-xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
                  <Users className="w-8 h-8 sm:w-10 sm:h-10 text-white" />
                </div>
              </div>
              <h4 className="text-2xl sm:text-2xl md:text-3xl font-display font-semibold text-blue-900 mb-3 sm:mb-4">Easy Collaboration</h4>
              <p className="text-sm sm:text-base md:text-lg text-gray-600 leading-relaxed">
                Share data seamlessly across all tools with single sign-on authentication 
                and real-time synchronization.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
              className="group bg-white border-2 border-gray-100 rounded-xl sm:rounded-2xl p-6 sm:p-8 md:p-10 text-center hover:border-yellow-300 hover:shadow-xl sm:hover:shadow-2xl transition-all duration-300"
            >
              <div className="relative inline-block mb-4 sm:mb-6 md:mb-8">
                <div className="absolute inset-0 bg-blue-800 rounded-xl blur-lg opacity-0 group-hover:opacity-20 transition-opacity duration-300" />
                <div className="relative w-16 h-16 sm:w-20 sm:h-20 bg-blue-800 rounded-xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
                  <Sparkles className="w-8 h-8 sm:w-10 sm:h-10 text-white" />
                </div>
              </div>
              <h4 className="text-2xl sm:text-2xl md:text-3xl font-display font-semibold text-blue-900 mb-3 sm:mb-4">Modern & Intuitive</h4>
              <p className="text-sm sm:text-base md:text-lg text-gray-600 leading-relaxed">
                Beautiful, user-friendly interfaces designed for efficiency and ease of use, 
                accessible from any device.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative bg-gradient-to-br from-blue-900 via-blue-800 to-blue-900 border-t border-blue-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 md:py-16">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 sm:gap-6">
            <div className="flex items-center space-x-3 sm:space-x-4">
              <div className="relative">
                <div className="absolute inset-0 bg-yellow-400 rounded-xl blur-md opacity-30" />
                <div className="relative w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-xl flex items-center justify-center shadow-lg">
                  <Compass className="w-5 h-5 sm:w-6 sm:h-6 md:w-7 md:h-7 text-white" />
                </div>
              </div>
              <div>
                <p className="text-white font-display font-bold text-lg sm:text-xl">Masonic Tools</p>
                <p className="text-xs sm:text-sm text-blue-200 font-serif italic">Professional Unit Management</p>
              </div>
            </div>
            <div className="text-center md:text-right">
              <p className="text-sm sm:text-base text-blue-100 font-medium">© {new Date().getFullYear()} Masonic Tools. All rights reserved.</p>
              <div className="flex flex-wrap gap-2 sm:gap-4 mt-2 justify-center md:justify-end">
                <Link to="/privacy" className="text-xs sm:text-sm text-blue-300 hover:text-yellow-400 transition-colors">Privacy Policy</Link>
                <span className="text-blue-300 hidden sm:inline">•</span>
                <Link to="/terms" className="text-xs sm:text-sm text-blue-300 hover:text-yellow-400 transition-colors">Terms & Conditions</Link>
              </div>
              <p className="text-xs sm:text-sm text-blue-300 font-serif italic mt-2">Empowering units with modern technology</p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
